import app from '../data/app.svelte.js';
import persistent from '../data/persistent.svelte.js';
import { currentSession, currentSessionId } from '../data/session.svelte.js';
import { fetchApi } from './api.svelte.js';
import { goto } from './navigation.svelte.js';
import { normalizeTelephone } from './phone.js';
import { toolDefinitions } from '@shared/tool-definitions.js';
import { labelDate } from '@shared/dates.js';
import { locale as widgetLocale } from 'svelte-i18n';
import { get } from 'svelte/store';

/**
 * Booking tool registry — assembles the shared (pure) tool definitions with the
 * client-side `execute` functions. The single source of truth shared by every
 * AI integration ("track"):
 *
 * - WebMCP (`webmcp.svelte.js`) registers these tools for external browser
 *   agents via `document.modelContext`.
 * - The built-in chat agent runs `executeTool()` locally while the Worker maps the
 *   definitions to the active provider's tool format.
 * - The on-device Prompt API driver receives the same tools.
 *
 * The definitions (name/description/inputSchema) live in
 * `shared/tool-definitions.js` so the Worker can import them without pulling in
 * browser code. The `execute` functions below always run client-side: they
 * read/write `currentSession` (the same source of truth the UI uses) and call the
 * Salonhub API. No tool ever finalizes a booking: `stage_appointment` only stages
 * the selection so the chat can show the confirmation card + button; pressing that
 * button (in the chatbot) is what advances the normal booking flow, so a human
 * always performs the only irreversible action. A tool's `execute` must not assume
 * which agent invoked it.
 *
 * WebMCP has no chat UI to render that confirmation card, so it gets one extra
 * tool the chat never sees — `confirm_appointment` — which re-checks the staged
 * slot and, on success, navigates the customer to the widget's own confirm page so
 * the human still presses the final book button. See `webmcpTools` below.
 */

/* Wrap a string as an MCP/WebMCP-style tool result. This is the canonical result
   shape; each driver adapts it (Claude → tool_result block, Prompt API → string). */
function text(message) {
    return { content: [{ type: 'text', text: message }] };
}

/* Every date the model sees carries its natural-language label — small models
   misderive weekdays from bare ISO dates (both when resolving "next Wednesday"
   and when writing a date back to the customer), so give them the label to
   copy instead. In the agent's language, falling back to the widget locale
   (same fallback as fetchApi). */
function describeDate(date, language) {
    const label = labelDate(date, language || get(widgetLocale));
    return label ? `${date} (${label})` : date;
}

/* Build FormData pre-filled with the session/client/salon every endpoint needs */
function sessionForm() {
    const form = new FormData();
    form.append('session', currentSessionId);
    form.append('client', currentSession.client);
    form.append('salon', currentSession.salon);
    return form;
}

/* Whether employee must be chosen before treatment (mirrors navigation flow) */
function isEmployeeFirst() {
    return app.settings.stepsOrder === 'employee,treatment'
        && currentSession.origin !== 'google-reserve';
}

/* Match a treatment/employee by exact id, exact name, then partial name */
function matchByIdOrName(items, query) {
    if (query === undefined || query === null) return null;

    const q = String(query).trim().toLowerCase();
    if (!q) return null;

    return items.find(item => String(item.id) === q)
        || items.find(item => (item.name || '').toLowerCase() === q)
        || items.find(item => (item.name || '').toLowerCase().includes(q))
        || null;
}

/* Canonicalise a time to "HH:MM" so a model-supplied value matches the API's
   value despite formatting differences (e.g. "9:00", "09:00", "09:00:00"). */
function normalizeTime(value) {
    const match = String(value ?? '').trim().match(/^(\d{1,2}):(\d{2})/);
    if (!match) return String(value ?? '').trim();
    return `${match[1].padStart(2, '0')}:${match[2]}`;
}


/* --- API loaders (faithful to the step components) --- */

async function loadTreatments(employeeId, language) {
    const form = sessionForm();
    let command = 'get';

    if (isEmployeeFirst() && employeeId !== null && employeeId !== undefined) {
        command = 'getForEmployee';
        form.append('employee', employeeId);
    }

    const data = await fetchApi({
        endpoint: `OnlineAppointment.Remote.Treatments/${command}`,
        data: form,
        language
    });

    const raw = data.treatments || {};

    // Tag each treatment with the name of the group it belongs to. Salons often split
    // an otherwise identical treatment across groups by who it's for (e.g. a "Dames" vs
    // "Heren" section), and that group name is the only signal of the distinction — so
    // carry it through the flatten instead of discarding it.
    return app.settings.treatmentGroups
        .filter(group => group.id in raw)
        .flatMap(group => (raw[group.id] || [])
            .filter(item => item.type === 'treatment')
            .map(item => ({ ...item, group: group.name || null })));
}

async function loadEmployees(treatmentId, language) {
    const form = sessionForm();
    let command = 'get';

    if ((app.settings.stepsOrder === 'treatment,employee' || currentSession.origin === 'google-reserve')
        && treatmentId !== null && treatmentId !== undefined) {
        command = 'getForTreatment';
        form.append('treatment', treatmentId);
    }

    const data = await fetchApi({
        endpoint: `OnlineAppointment.Remote.Employees/${command}`,
        data: form,
        language
    });

    return data.employees || [];
}

async function loadDates(treatmentId, employeeId, language) {
    const form = sessionForm();
    form.append('treatment', treatmentId);
    form.append('employee', employeeId);
    form.append('start', 0);
    form.append('limit', 90); // ~3 months ahead; the API still caps at the salon's maximum

    const data = await fetchApi({
        endpoint: 'OnlineAppointment.Remote.Dates/get',
        data: form,
        language
    });

    return (data.dates || []).map(entry => entry.date);
}

async function loadTimes(treatmentId, employeeId, date, language) {
    const form = sessionForm();
    form.append('treatment', treatmentId);
    form.append('employee', employeeId);
    form.append('date', date);

    const data = await fetchApi({
        endpoint: 'OnlineAppointment.Remote.Times/get',
        data: form,
        language
    });

    return (data.times || []).map(entry => entry.time);
}


/* --- Selection resolution (pure: resolves without mutating the session) --- */

/**
 * Resolve treatment + employee from agent-supplied names/ids, honouring the
 * salon's step order. Returns { treatment, employee } or { error } with the
 * valid options so the agent can self-correct.
 */
async function resolveSelection({ treatment: treatmentQuery, employee: employeeQuery }, language) {
    let treatment;
    let employee;

    if (isEmployeeFirst()) {
        const employees = await loadEmployees(null, language);
        employee = employeeQuery
            ? matchByIdOrName(employees.filter(e => e.type === 'employee'), employeeQuery)
            : employees.find(e => e.type === 'any');

        if (!employee) {
            const names = employees.filter(e => e.type === 'employee').map(e => e.name);
            return { error: employeeQuery
                ? `No employee matched "${employeeQuery}". Available employees: ${names.join(', ') || 'none'}.`
                : `This salon requires choosing an employee first. Available employees: ${names.join(', ') || 'none'}.` };
        }

        const treatments = await loadTreatments(employee.id, language);
        treatment = matchByIdOrName(treatments, treatmentQuery);

        if (!treatment) {
            return { error: `No treatment matched "${treatmentQuery}" for ${employee.name}. Available treatments: ${treatments.map(t => t.name).join(', ') || 'none'}.` };
        }
    } else {
        const treatments = await loadTreatments(null, language);
        treatment = matchByIdOrName(treatments, treatmentQuery);

        if (!treatment) {
            return { error: `No treatment matched "${treatmentQuery}". Available treatments: ${treatments.map(t => t.name).join(', ') || 'none'}.` };
        }

        const employees = await loadEmployees(treatment.id, language);
        employee = employeeQuery
            ? matchByIdOrName(employees.filter(e => e.type === 'employee'), employeeQuery)
            : employees.find(e => e.type === 'any');

        if (!employee) {
            const names = employees.filter(e => e.type === 'employee').map(e => e.name);
            return { error: employeeQuery
                ? `No employee matched "${employeeQuery}". Available employees for ${treatment.name}: ${names.join(', ') || 'none'}.`
                : `This treatment has no "no preference" option; an employee must be chosen. Available employees: ${names.join(', ') || 'none'}.` };
        }
    }

    return { treatment, employee };
}


/* --- Tool implementations --- */

async function listTreatments({ employee } = {}, language) {
    let employeeId = null;

    if (employee) {
        const employees = await loadEmployees(null, language);
        const match = matchByIdOrName(employees.filter(e => e.type === 'employee'), employee);
        if (!match) {
            return text(`No employee matched "${employee}".`);
        }
        employeeId = match.id;
    }

    const treatments = await loadTreatments(employeeId, language);

    // Lean output: book by name (id is a matching fallback). `length` (minutes) lets the
    // agent answer "how long does it take". `group` names the section the treatment sits
    // in, so the agent can tell apart same-named variants (e.g. women's vs men's). Collapse
    // price to its display string, shown only when the widget would show it (positive value
    // + visible).
    const list = treatments.map(t => ({
        id: t.id,
        name: t.name,
        ...(t.group ? { group: t.group } : {}),
        length: t.length,
        ...(t.price && t.price.value > 0 && t.price.visible ? { price: t.price.name } : {})
    }));

    // Salons can hide prices on purpose. A silently missing `price` reads as "unknown" to
    // a small model, which then fills the gap with an invented amount — so when any price
    // is hidden, say so explicitly next to the list instead of leaving the field out.
    if (list.some(t => !('price' in t))) {
        return text(JSON.stringify({
            treatments: list,
            pricing: 'Treatments without a "price" field have no price available: this salon has chosen not to publish those prices online.'
        }));
    }

    return text(JSON.stringify(list));
}

async function listEmployees({ treatment } = {}, language) {
    let treatmentId = null;

    if (treatment) {
        const treatments = await loadTreatments(null, language);
        const match = matchByIdOrName(treatments, treatment);
        if (!match) {
            return text(`No treatment matched "${treatment}".`);
        }
        treatmentId = match.id;
    }

    const employees = await loadEmployees(treatmentId, language);

    const list = employees
        .filter(e => e.type === 'employee')
        .map(e => ({ id: e.id, name: e.name }));

    const anyAvailable = employees.some(e => e.type === 'any');

    return text(JSON.stringify({ employees: list, noPreferenceAvailable: anyAvailable }));
}

async function getAvailability({ treatment, employee, date }, language) {
    const resolved = await resolveSelection({ treatment, employee }, language);
    if (resolved.error) {
        return text(resolved.error);
    }

    if (date) {
        const times = await loadTimes(resolved.treatment.id, resolved.employee.id, date, language);
        if (times.length === 0) {
            return text(`No times available on ${describeDate(date, language)} for ${resolved.treatment.name} with ${resolved.employee.name}. Call get_availability without a date to get the available dates, then offer those to the customer.`);
        }
        return text(JSON.stringify({
            date,
            label: labelDate(date, language || get(widgetLocale)),
            times
        }));
    }

    const dates = await loadDates(resolved.treatment.id, resolved.employee.id, language);
    if (dates.length === 0) {
        return text(`No available dates found for ${resolved.treatment.name} with ${resolved.employee.name}.`);
    }

    return text(JSON.stringify({
        treatment: resolved.treatment.name,
        employee: resolved.employee.name,
        availableDates: dates.map(d => ({ date: d, label: labelDate(d, language || get(widgetLocale)) })),
        hint: 'No other dates are available. Call get_availability again with one of these dates (the "date" value) to list available times; use the "label" when writing the date to the customer.'
    }));
}

async function stageAppointment({ treatment, employee, date, time, customer }, language) {
    /* 0. Already booked? A confirmed session holds a finished appointment — never
       overwrite it. The customer must start a fresh session for a new booking. */
    if (currentSession.status === 'confirmed') {
        return text('An appointment has already been booked in this session, so another cannot be booked here. Tell the customer to click the "make another appointment" button to start a new booking.');
    }

    /* 1. Resolve treatment + employee */
    const resolved = await resolveSelection({ treatment, employee }, language);
    if (resolved.error) {
        return text(resolved.error);
    }

    /* 2. Validate the requested date + time against real availability.
       Fetch the times for that date directly (this also validates the date, and
       avoids the slow full-range Dates/get scan). Match the time TOLERANTLY: the
       model may format it slightly differently than the API ("9:00" vs "09:00",
       or with seconds), so compare on a canonical "HH:MM" form. We then store the
       API's exact value so the confirmation page and Appointments/create match —
       the real availability conflict is enforced there (409) on the human's
       final "Book" press. */
    const times = await loadTimes(resolved.treatment.id, resolved.employee.id, date, language);
    const matchedTime = times.find(t => normalizeTime(t) === normalizeTime(time));

    if (!matchedTime) {
        if (times.length === 0) {
            const dates = await loadDates(resolved.treatment.id, resolved.employee.id, language);
            return text(`No times are available on ${describeDate(date, language)} for ${resolved.treatment.name} with ${resolved.employee.name}. Available dates: ${dates.slice(0, 30).map(d => describeDate(d, language)).join(', ') || 'none'}. Call get_availability with one of these dates to see its times.`);
        }
        return text(`${time} is not available on ${describeDate(date, language)}. Available times: ${times.join(', ') || 'none'}.`);
    }

    /* Write the selection into the session, exactly like the UI steps do */
    currentSession.treatment = resolved.treatment;
    currentSession.employee = resolved.employee;
    persistent.treatment = resolved.treatment.id;
    currentSession.date = date;
    currentSession.time = matchedTime;
    currentSession.type = 'appointment';

    /* 4. Check the customer (blocked / rejected / verification required / ok)
       Fall back to the already-known customer (same precedence as the UI:
       explicit argument > current session > saved/persistent), so returning
       customers don't have to re-supply details. */
    const provided = customer || {};
    const known = persistent.customer || {};
    const sessionCustomer = currentSession.customer || {};

    const candidate = {
        firstname: provided.firstname || sessionCustomer.firstname || known.firstname || '',
        lastname: provided.lastname || sessionCustomer.lastname || known.lastname || '',
        email: provided.email || sessionCustomer.email || known.email || '',
        telephone: provided.telephone || sessionCustomer.telephone || known.telephone || ''
    };

    const missing = ['firstname', 'lastname', 'email', 'telephone'].filter(field => !candidate[field]);
    if (missing.length > 0) {
        return text(`The booking is ready except for these customer details: ${missing.join(', ')}. Provide them in the "customer" argument and call stage_appointment again.`);
    }

    const form = sessionForm();
    form.append('guid', persistent.guid);
    form.append('name[firstname]', candidate.firstname);
    form.append('name[lastname]', candidate.lastname);
    form.append('email', candidate.email);
    form.append('telephone', normalizeTelephone(candidate.telephone));

    const result = await fetchApi({
        endpoint: 'OnlineAppointment.Remote.Customer/check',
        data: form,
        language
    });

    const summary = `${resolved.treatment.name} with ${resolved.employee.name} on ${describeDate(date, language)} at ${time}`;

    if (result.status === 'blocked') {
        return text('This customer is blocked from booking at this salon. The appointment was not staged.');
    }

    if (result.status === 'rejected') {
        return text('This customer cannot book at this salon (rejected). The appointment was not staged.');
    }

    if (result.status === 'validate') {
        // Verification required: stash the unverified customer. The agent collects the
        // code in the chat (submit_verification_code) — we do NOT navigate to the
        // widget's verification screen, which would leave the chat.
        currentSession.pendingCustomer = candidate;

        const method = app.settings.customer.validate.method || 'email';
        const target = method === 'sms' ? candidate.telephone : candidate.email;

        return text(`Identity verification is required before this booking can continue. A ${method} verification code has been sent to ${target}. Ask the customer for the ${method === 'sms' ? 'SMS' : 'email'} code, then call submit_verification_code with it.`);
    }

    if (result.status === 'ok') {
        // Stage the booking but do NOT navigate — the confirmation card with its button
        // is shown in the chat, and pressing it navigates on to finalize the booking.
        persistent.customer = candidate;
        currentSession.customer = candidate;

        return text('The appointment is staged and a summary card with the details and a confirm button is now shown to the customer. Ask them to check the details below and press the confirm button — do NOT repeat the treatment, employee, date, or time in your reply. This tool does not create the appointment itself; the customer must press the button. Nothing is booked yet: if the customer asks for a different treatment, employee, date, or time, call stage_appointment again with the new details and the card is replaced.');
    }

    return text('Could not verify the customer details (unexpected response). The appointment was not staged.');
}

async function submitVerificationCode({ code }, language) {
    if (!currentSession.pendingCustomer) {
        return text('There is no pending booking awaiting verification. Call stage_appointment first.');
    }

    const form = sessionForm();
    form.append('guid', persistent.guid);
    form.append('code', String(code));

    const result = await fetchApi({
        endpoint: 'OnlineAppointment.Remote.Customer/validate',
        data: form,
        language
    });

    if (result.status === 'ok') {
        // Booking now fully staged — show the confirmation card in the chat (no
        // navigation); pressing its button navigates on to finalize.
        persistent.customer = currentSession.pendingCustomer;
        currentSession.customer = currentSession.pendingCustomer;
        currentSession.pendingCustomer = null;

        return text('Verification successful. A summary card with the appointment details and a confirm button is now shown to the customer. Ask them to check the details below and press the confirm button — do NOT repeat the treatment, employee, date, or time. The customer must press the button to finalize. Nothing is booked yet: if the customer asks for a different treatment, employee, date, or time, call stage_appointment again with the new details and the card is replaced.');
    }

    if (result.status === 'validate') {
        return text('That verification code is incorrect. Ask the customer to re-check the code and call submit_verification_code again.');
    }

    return text('Verification failed (unexpected response). The customer may need to complete the booking through the widget UI.');
}

/**
 * Re-check that a staged appointment is genuinely available on the server — an
 * independent guard for the chat's confirm button against an agent hallucination
 * or a slot that was taken since it was staged. Takes the staged selection
 * (treatment object, optional employee, date, time).
 *
 * Uses Times/check, which answers for ONE specific slot, instead of Times/get: the
 * latter returns a filtered, non-deterministic list, so a slot that's actually free can
 * be absent just because the day's result set shifted — a false negative that would wrongly
 * keep the button disabled. "No preference" is a valid choice → employee defaults to -1.
 * Any failure (missing input, network) returns false: better to keep the button disabled
 * than let a human confirm a slot we couldn't verify.
 */
export async function checkAvailability({ treatment, employee, date, time } = {}, language) {
    if (!treatment?.id || !date || !time) return false;

    try {
        const data = await fetchApi({
            endpoint: 'OnlineAppointment.Remote.Times/check',
            params: {
                client: currentSession.client,
                salon: currentSession.salon,
                treatment: treatment.id,
                date,
                time,
                employee: employee?.id || -1
            },
            language
        });
        return data?.available === true;
    } catch {
        return false;
    }
}

/* The widget step the customer is currently on, from the URL (/{session}/{step}). */
function currentStep() {
    const segments = window.location.pathname.split('/').filter(Boolean);
    return segments[segments.length - 1] || null;
}

/* The customer's in-progress widget selection, for agent context. */
export function currentSelection() {
    return {
        step: currentStep(),
        treatment: currentSession.treatment?.name || null,
        employee: currentSession.employee?.name || null,
        date: currentSession.date || null,
        time: currentSession.time || null
    };
}

/* A compact note describing the current selection, prepended to a user message by
   the drivers when the customer has navigated the widget mid-conversation — so the
   agent picks up the change without restarting the conversation. */
export function selectionNote(selection = currentSelection()) {
    const v = (x) => x || 'none';
    return `[Current widget state — step: ${selection.step || 'unknown'}, treatment: ${v(selection.treatment)}, employee: ${v(selection.employee)}, date: ${selection.date ? describeDate(selection.date) : 'none'}, time: ${v(selection.time)}]`;
}

async function getCustomer(_input, language) {
    /* Same precedence as stage_appointment's fallback: current session over the
       saved/persistent customer. */
    const sessionCustomer = currentSession.customer || {};
    const saved = persistent.customer || {};

    const customer = {
        firstname: sessionCustomer.firstname || saved.firstname || '',
        lastname: sessionCustomer.lastname || saved.lastname || '',
        email: sessionCustomer.email || saved.email || '',
        telephone: sessionCustomer.telephone || saved.telephone || ''
    };

    const fields = ['firstname', 'lastname', 'email', 'telephone'];
    const onFile = fields.filter(field => customer[field]);
    const missing = fields.filter(field => !customer[field]);

    const result = {
        known: onFile.length > 0,
        previousTreatment: await resolvePreviousTreatment(language),
        // What the customer has chosen in the widget so far (step + selection).
        currentSelection: currentSelection()
    };

    if (result.known) {
        /* Return presence + the name (for a personalised confirmation) only.
           Contact values are deliberately withheld from the agent —
           stage_appointment reuses the stored values automatically. */
        result.name = [customer.firstname, customer.lastname].filter(Boolean).join(' ') || null;
        result.onFile = onFile;
        result.missing = missing;
        result.readyToBook = missing.length === 0;
    }

    return text(JSON.stringify(result));
}

/* Resolve the treatment the customer previously chose: the current session's
   selection (a full object) if present, otherwise the treatment id saved from a
   past booking, resolved to a name via the live treatment list. Returns
   { id, name } or null. */
async function resolvePreviousTreatment(language) {
    const current = currentSession.treatment;
    if (current && current.id) {
        return { id: current.id, name: current.name || null };
    }

    const savedId = persistent.treatment;
    if (!savedId) return null;

    try {
        const treatments = await loadTreatments(null, language);
        const match = treatments.find(t => String(t.id) === String(savedId));
        return match ? { id: match.id, name: match.name } : { id: savedId, name: null };
    } catch {
        return { id: savedId, name: null };
    }
}

function getSalonInfo() {
    const salon = app.salon || {};
    const locality = [salon.postalcode, salon.city].filter(Boolean).join(' ');
    const address = [salon.address, locality].filter(Boolean).join(', ');

    return text(JSON.stringify({
        name: salon.name || null,
        telephone: salon.telephone || null,
        email: salon.email || null,
        address: address || null,
        website: salon.website || null
    }));
}


/* Whether a complete booking is staged and ready for the customer to confirm —
   the same conditions the chat's confirm card and the widget's Confirm page use.
   Guards confirm_appointment against navigating to Confirm with an incomplete
   session (which the authGuard would just bounce back to an earlier step). */
function stagedBookingComplete() {
    return currentSession?.type === 'appointment'
        && currentSession?.status === 'started'
        && !!currentSession?.treatment
        && !!currentSession?.employee
        && !!currentSession?.date
        && !!currentSession?.time
        && !!currentSession?.customer?.firstname
        && !!currentSession?.customer?.email;
}

/**
 * WebMCP-only: finalize a staged booking on the customer's behalf. There is no
 * chat card to press outside the chatbot, so this is the WebMCP equivalent —
 * it re-runs the same independent server availability check the chat's confirm
 * button uses (see verifyStagedCard / checkAvailability) and, only if the slot is
 * still bookable, navigates the customer to the widget's own Confirm page. The
 * human still presses the final book button there; this tool never creates the
 * appointment itself. On any failure it reports back to the agent and does NOT
 * navigate. Not registered for the chat agent, which has its own confirm button.
 */
async function confirmAppointment(_input, language) {
    if (!stagedBookingComplete()) {
        return text('No appointment is staged to confirm. Call stage_appointment first with a complete treatment, date, time, and customer details.');
    }

    const staged = {
        treatment: currentSession.treatment,
        employee: currentSession.employee,
        date: currentSession.date,
        time: currentSession.time
    };

    const available = await checkAvailability(staged, language);
    if (!available) {
        return text('That time slot is no longer available, so the booking cannot be confirmed. Call get_availability again to find other times, then stage_appointment with a new slot.');
    }

    // Still bookable — hand off to the widget's Confirm page for the human to finalize.
    goto('confirm', 'right');
    return text('The slot is still available. The customer has been taken to the confirmation page in the widget to review the details and press the book button to finalize. This tool does not create the appointment itself.');
}


/* --- The registry --- */

/* Client-side executors, keyed by tool name. Merged with the shared definitions
   below so the definition stays the single source of truth. */
const executors = {
    list_treatments: listTreatments,
    list_employees: listEmployees,
    get_availability: getAvailability,
    get_salon_info: getSalonInfo,
    get_customer: getCustomer,
    stage_appointment: stageAppointment,
    submit_verification_code: submitVerificationCode,
    confirm_appointment: confirmAppointment
};

/**
 * The canonical booking tool registry: each shared definition with its
 * client-side `execute` attached. Consumed by the chat agent (Prompt API driver)
 * and the base of the WebMCP set. Do not execute these directly per-track — go
 * through `executeTool()` so error isolation is uniform.
 */
export const bookingTools = toolDefinitions.map(definition => ({
    ...definition,
    execute: executors[definition.name]
}));

/* WebMCP-only tool definitions — deliberately NOT in shared/tool-definitions.js,
   so neither chat backend (Worker or Prompt API) ever advertises them to its
   agent. confirm_appointment only makes sense where there is no chat confirm card. */
const webmcpOnlyDefinitions = [
    {
        name: 'confirm_appointment',
        description: "Finalize the booking staged by stage_appointment. Re-checks that the staged slot is still available; if so, takes the customer to the widget's confirmation page to review and press the book button, and if not, reports that it's gone. Call this once the customer has agreed to the staged appointment.",
        annotations: { readOnlyHint: false },
        inputSchema: { type: 'object', properties: {} }
    }
];

/**
 * The full tool set exposed to an in-browser WebMCP agent: the shared booking
 * tools plus the WebMCP-only tools above. The chat agent uses `bookingTools`
 * instead, so it never sees confirm_appointment.
 */
export const webmcpTools = [
    ...bookingTools,
    ...webmcpOnlyDefinitions.map(definition => ({
        ...definition,
        execute: executors[definition.name]
    }))
];

/**
 * Run a tool by name with uniform error isolation. Always resolves to the
 * canonical result shape ({ content: [{ type: 'text', text }] }) — a thrown
 * error is surfaced to the agent rather than rejecting (and never breaks the
 * widget). Shared by the WebMCP runtime and the in-widget chat agent. Resolves
 * against the WebMCP superset so it can run WebMCP-only tools too; chat drivers
 * gate their own tool exposure via `bookingTools`. */
export async function executeTool(name, input, language) {
    const tool = webmcpTools.find(t => t.name === name);
    if (!tool || typeof tool.execute !== 'function') {
        return text(`Unknown tool "${name}".`);
    }

    // `language` is the agent's API language, passed through to the tool's fetchApi
    // calls. Omitted (WebMCP, Claude) → fetchApi falls back to the widget locale.
    try {
        return await tool.execute(input || {}, language);
    } catch (error) {
        return text(`The booking service returned an error (${error?.code || error?.message || 'unknown'}). Please try again or complete the booking in the widget.`);
    }
}
